/**
 * MTA 统一技能注册表
 * 
 * 将 20+ 独立 MCP 工具收拢为统一的 skill 分发系统。
 * AI 根据工具 description 中的技能目录自主选择 skill，
 * 工具内部做简单路由分发到具体实现。
 * 
 * 设计原则：
 * - 每个 skill 自描述（name + description + paramsSchema + handler）
 * - SkillRegistry 负责注册、查询、生成统一工具的 description
 * - 不做自然语言推断，由 AI 根据 description 选择 skill
 * 
 * @module core/skillRegistry
 */

import type { MCPArgs } from './toolTypes.js';

/**
 * 技能分类
 */
export type SkillCategory = 
  | 'standards'      // 规范获取
  | 'workflow'       // 工作流 / 子代理式路由
  | 'project'        // 项目分析与配置
  | 'templates'      // 模板系统
  | 'clone'          // 项目克隆
  | 'troubleshoot'   // 问题诊断
  | 'agents'         // Agent 管理
  | 'sketch';        // Sketch 设计稿工具

/**
 * MCP 标准响应格式
 */
export interface MCPContent {
  content: Array<{ type: string; text: string }>;
}

/**
 * 参数描述（用于生成可读的参数提示）
 */
export interface ParamHint {
  name: string;
  type: string;
  required?: boolean;
  description: string;
}

/**
 * 技能定义
 */
export interface SkillDefinition {
  /** 技能标识（即 skill 枚举值） */
  name: string;
  /** 分类 */
  category: SkillCategory;
  /** 一句话说明（供 AI 阅读，选择 skill 的依据） */
  description: string;
  /** 关键参数提示（供 AI 了解需要传什么参数） */
  paramHints: ParamHint[];
  /** 执行函数 */
  handler: (params: Record<string, unknown>) => Promise<MCPContent>;
}

/**
 * 技能注册表
 * 
 * 管理所有 MTA 业务 skill，提供注册、查询和统一工具描述生成能力。
 */
export class SkillRegistry {
  private skills: Map<string, SkillDefinition> = new Map();

  /**
   * 注册一个 skill
   */
  register(skill: SkillDefinition): void {
    if (this.skills.has(skill.name)) {
      throw new Error(`Skill "${skill.name}" 已注册，不允许重复注册`);
    }
    this.skills.set(skill.name, skill);
  }

  /**
   * 批量注册
   */
  registerAll(skills: SkillDefinition[]): void {
    for (const skill of skills) {
      this.register(skill);
    }
  }

  /**
   * 获取指定 skill
   */
  get(name: string): SkillDefinition | undefined {
    return this.skills.get(name);
  }

  /**
   * 获取所有 skill 名称（枚举值列表）
   */
  getSkillNames(): string[] {
    return Array.from(this.skills.keys());
  }

  /**
   * 按分类获取 skill 列表
   */
  getByCategory(category: SkillCategory): SkillDefinition[] {
    return Array.from(this.skills.values()).filter(s => s.category === category);
  }

  /**
   * 获取全部 skill 定义
   */
  getAll(): SkillDefinition[] {
    return Array.from(this.skills.values());
  }

  /**
   * 生成 mta 统一工具的 description 文本
   * 
   * 按分类输出所有 skill 的名称和说明，供 AI 阅读后选择。
   * 格式紧凑，控制 token 消耗。
   */
  generateToolDescription(): string {
    const categories: Record<SkillCategory, string> = {
      standards: '规范获取',
      workflow: '工作流路由',
      project: '项目分析与配置',
      templates: '模板系统',
      clone: '项目克隆',
      troubleshoot: '问题诊断',
      agents: 'Agent 管理',
      sketch: 'Sketch 设计稿工具',
    };

    const lines: string[] = [
      'MTA 智能编码助手 - 统一技能入口。',
      '通过 skill 参数选择要执行的技能。优先使用 params 传参；若客户端不支持嵌套对象，也可直接把参数平铺到顶层。',
      '复杂任务建议先调用 recommend_workflow，确定 workflow 后再用 advance_workflow 逐步推进，并在代码生成前调用 validate_workflow。',
      '',
      '可用技能：',
    ];

    // 按分类分组输出
    for (const [cat, label] of Object.entries(categories)) {
      const skills = this.getByCategory(cat as SkillCategory);
      if (skills.length === 0) continue;

      lines.push(`\n[${label}]`);
      for (const skill of skills) {
        // 格式：  skill_name - 描述 | 参数: param1(必填), param2
        const paramStr = skill.paramHints.length > 0
          ? ' | 参数: ' + skill.paramHints
              .map(p => p.required ? `${p.name}(必填)` : p.name)
              .join(', ')
          : '';
        lines.push(`  ${skill.name} - ${skill.description}${paramStr}`);
      }
    }

    return lines.join('\n');
  }

  /**
   * 生成 mta 统一工具的 JSON Schema（inputSchema）
   */
  generateInputSchema(): Record<string, unknown> {
    return {
      type: 'object',
      properties: {
        skill: {
          type: 'string',
          enum: this.getSkillNames(),
          description: '要执行的技能名称',
        },
        params: {
          type: 'object',
          description: '该技能所需的参数（参见工具描述中各技能的参数说明）',
          additionalProperties: true,
        },
      },
      additionalProperties: true,
      required: ['skill'],
    };
  }

  /**
   * 执行一个 skill
   */
  async execute(skillName: string, params: Record<string, unknown>): Promise<MCPContent> {
    const skill = this.skills.get(skillName);
    if (!skill) {
      const available = this.getSkillNames().join(', ');
      return {
        content: [{
          type: 'text',
          text: JSON.stringify({
            error: `未知技能: ${skillName}`,
            availableSkills: available,
            hint: '请从可用技能列表中选择一个有效的 skill 名称',
          }, null, 2),
        }],
      };
    }

    return skill.handler(params);
  }

  /**
   * 获取注册统计
   */
  getStats(): { total: number; byCategory: Record<string, number> } {
    const byCategory: Record<string, number> = {};
    for (const skill of this.skills.values()) {
      byCategory[skill.category] = (byCategory[skill.category] || 0) + 1;
    }
    return { total: this.skills.size, byCategory };
  }
}

/**
 * 全局技能注册表单例
 */
let globalRegistry: SkillRegistry | null = null;

export function getSkillRegistry(): SkillRegistry {
  if (!globalRegistry) {
    globalRegistry = new SkillRegistry();
  }
  return globalRegistry;
}

/**
 * 重置注册表（测试用）
 */
export function resetSkillRegistry(): void {
  globalRegistry = null;
}
